import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, IconButton, CssBaseline, makeStyles } from '@material-ui/core';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import WaitingSlide from '../components/stream/WaitingSlide';
import HeadToHeadSlide from '../components/stream/HeadToHeadSlide';
import MatchHistorySlide from '../components/stream/MatchHistorySlide';
import VerdictGridSlide from '../components/stream/VerdictGridSlide';
import DifficultyTagsSlide from '../components/stream/DifficultyTagsSlide';
import PaceSlide from '../components/stream/PaceSlide';
import useLocalStorage from '../hooks/useLocalStorage';

// How long the controls stay up after the mouse stops moving
const CONTROLS_TIMEOUT = 2500;

// Every slide is laid out against this box and the whole box is then scaled to
// whatever the window is, so the deck is the same picture at any window size and
// any zoom rather than reflowing under them. 16:9, the shape a stream is.
const DESIGN_WIDTH = 1280;
const DESIGN_HEIGHT = 720;

const useStyles = makeStyles(theme => ({
    // the slideshow is opened in a window of its own and takes all of it: none of
    // the navigation the rest of the site is wrapped in belongs on a stream.
    // Text colour is set here as well as by the baseline, so that anything which
    // inherits it rather than carrying its own has the theme's ink and not the
    // browser's black.
    root: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        overflow: 'hidden'
    },
    // the box a slide is drawn in, before it is scaled onto the window
    stage: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: DESIGN_WIDTH,
        height: DESIGN_HEIGHT,
        overflow: 'hidden'
    },
    controls: {
        transition: 'opacity 250ms',
        position: 'absolute'
    },
    hidden: {
        opacity: 0,
        pointerEvents: 'none'
    },
    arrow: {
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.35)'
    },
    footer: {
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: theme.spacing(2)
    },
    dot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        backgroundColor: 'rgba(255, 255, 255, 0.25)'
    },
    dotCurrent: {
        backgroundColor: theme.palette.primary.main
    }
}));

// A time of day as the HH:MM an <input type='time'> wants
function toTimeValue(timestamp) {
    if (timestamp == null) return '';
    const date = new Date(timestamp);
    return `${("0" + date.getHours()).slice(-2)}:${("0" + date.getMinutes()).slice(-2)}`;
}

// How far the design box has to be scaled to sit inside the window. Zooming the
// browser changes the window in css pixels, so this follows a zoom the same way
// it follows a resize, and the slide keeps its proportions either way.
function useStageScale() {
    const frame = useRef(null);
    const [scale, setScale] = useState(1);
    useLayoutEffect(() => {
        const element = frame.current;
        if (element == null) return;
        const measure = () => {
            const { clientWidth, clientHeight } = element;
            if (clientWidth === 0 || clientHeight === 0) return;
            setScale(Math.min(clientWidth / DESIGN_WIDTH, clientHeight / DESIGN_HEIGHT));
        };
        measure();
        window.addEventListener('resize', measure);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(element);
        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, []);
    return [frame, scale];
}

function useFullscreen() {
    const [fullscreen, setFullscreen] = useState(false);
    useEffect(() => {
        const onChange = () => setFullscreen(document.fullscreenElement != null);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    const toggle = useCallback(() => {
        if (document.fullscreenElement != null) document.exitFullscreen();
        else document.documentElement.requestFullscreen?.();
    }, []);
    return [fullscreen, toggle];
}

// The controls fade away while the mouse is still, so that a slide left up on a
// stream is only the slide
function useIdleControls() {
    const [visible, setVisible] = useState(true);
    useEffect(() => {
        let timeout = setTimeout(() => setVisible(false), CONTROLS_TIMEOUT);
        const wake = () => {
            setVisible(true);
            clearTimeout(timeout);
            timeout = setTimeout(() => setVisible(false), CONTROLS_TIMEOUT);
        };
        window.addEventListener('mousemove', wake);
        return () => {
            window.removeEventListener('mousemove', wake);
            clearTimeout(timeout);
        };
    }, []);
    return visible;
}

function MatchStreamPage() {
    const { matchId } = useParams();
    const classes = useStyles();
    const [match, setMatch] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [slide, setSlide] = useState(0);
    const [frame, scale] = useStageScale();
    const [fullscreen, toggleFullscreen] = useFullscreen();
    const controlsVisible = useIdleControls();
    const [startTime, setStartTime] = useLocalStorage(`stream-start-${matchId}`, '');
    const [resumeTime, setResumeTime] = useLocalStorage(`stream-resume-${matchId}`, '');

    const getStream = useCallback(async () => {
        const response = await fetch(`/api/match/${matchId}/stream`, { credentials: 'include' });
        if (response.status !== 200) {
            setErrorMessage(response.status === 401 ?
                'Only the hosts of this match and system admins can open its stream' :
                'The stream could not be loaded');
            return;
        }
        const result = await response.json();
        if (result.status !== 'OK') {
            setErrorMessage(result.body?.errorMessages?.[0] ?? 'The stream could not be loaded');
            return;
        }
        setErrorMessage(null);
        setMatch(result.body);
    }, [matchId]);

    useEffect(() => {
        getStream();
    }, [getStream]);

    // the start time defaults to when the match is scheduled, until it is set
    useEffect(() => {
        if (match == null || startTime !== '') return;
        const scheduled = toTimeValue(match.scheduledStartTime);
        if (scheduled !== '') setStartTime(scheduled);
    }, [match, startTime, setStartTime]);

    const slides = useMemo(() => {
        if (match == null) return [];
        const statistics1 = match.statistics?.[match.contestant1] ?? null;
        const statistics2 = match.statistics?.[match.contestant2] ?? null;
        const label = statistics1?.matches?.find(row => row.matchId === match.matchId)?.label
            ?? statistics2?.matches?.find(row => row.matchId === match.matchId)?.label;
        const common = {
            tournamentName: match.tournamentName,
            matchLabel: label,
            contestant1: match.contestant1,
            contestant2: match.contestant2,
            statistics1,
            statistics2
        };
        const deck = [
            {
                key: 'waiting',
                label: 'Waiting',
                element: <WaitingSlide
                    {...common}
                    headline='Stream will start at'
                    time={startTime}
                    onTimeChange={setStartTime}
                />
            },
            {
                key: 'pause',
                label: 'Break',
                element: <WaitingSlide
                    {...common}
                    headline='Stream will resume at'
                    time={resumeTime}
                    onTimeChange={setResumeTime}
                />
            }
        ];
        // a friendly match belongs to no tournament, so there are no standings
        if (statistics1 != null || statistics2 != null) {
            deck.push(
                { key: 'summary', label: 'Summary', element: <HeadToHeadSlide {...common} /> },
                { key: 'history', label: 'Match History', element: <MatchHistorySlide {...common} /> },
                { key: 'verdicts', label: 'Verdicts and Grid Distribution', element: <VerdictGridSlide {...common} /> },
                { key: 'difficulty', label: 'Difficulty and Tags', element: <DifficultyTagsSlide {...common} /> },
                { key: 'pace', label: 'Pace', element: <PaceSlide {...common} /> }
            );
        }
        return deck;
    }, [match, startTime, setStartTime, resumeTime, setResumeTime]);

    const move = useCallback(step => {
        setSlide(slide => Math.min(Math.max(slide + step, 0), Math.max(slides.length - 1, 0)));
    }, [slides.length]);

    useEffect(() => {
        const onKeyDown = event => {
            // the arrow keys belong to a time being typed into
            if (event.target instanceof HTMLInputElement) return;
            if (event.key === 'ArrowRight') move(1);
            else if (event.key === 'ArrowLeft') move(-1);
            else if (event.key === 'Home') setSlide(0);
            else if (event.key === 'End') setSlide(slides.length - 1);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [move, slides.length]);

    // one root whatever there is to show, so that the frame the stage is measured
    // against is mounted from the first render rather than only once a match has
    // been loaded
    const ready = errorMessage == null && match != null && slides.length !== 0;
    const current = ready ? Math.min(slide, slides.length - 1) : 0;
    const controlClass = `${classes.controls} ${controlsVisible ? '' : classes.hidden}`;
    let content;
    if (errorMessage != null) {
        content = (
            <Box height='100%' display='flex' alignItems='center' justifyContent='center' p={6}>
                <Typography variant='h5' align='center'> {errorMessage} </Typography>
            </Box>
        );
    } else if (!ready) {
        content = (
            <Box height='100%' display='flex' alignItems='center' justifyContent='center'>
                <Typography variant='h5' color='textSecondary'> Loading… </Typography>
            </Box>
        );
    } else {
        content = slides[current].element;
    }

    return (
        <div className={classes.root} ref={frame}>
            <CssBaseline />
            <div
                className={classes.stage}
                style={{ transform: `translate(-50%, -50%) scale(${scale})` }}
            >
                {content}
            </div>

            {ready && <>
            <IconButton
                className={`${controlClass} ${classes.arrow}`}
                style={{ left: 16 }}
                onClick={() => move(-1)}
                disabled={current === 0}
            >
                <NavigateBeforeIcon fontSize='large' />
            </IconButton>
            <IconButton
                className={`${controlClass} ${classes.arrow}`}
                style={{ right: 16 }}
                onClick={() => move(1)}
                disabled={current === slides.length - 1}
            >
                <NavigateNextIcon fontSize='large' />
            </IconButton>

            <Box className={controlClass} style={{ top: 8, right: 8 }} display='flex' alignItems='center'>
                <Typography variant='caption' color='textSecondary'>
                    {current + 1} / {slides.length}
                </Typography>
                <IconButton onClick={toggleFullscreen} title='Full screen'>
                    {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
            </Box>

            <div className={`${controlClass} ${classes.footer}`}>
                {slides.map((entry, index) => (
                    <button
                        key={entry.key}
                        type='button'
                        title={entry.label}
                        aria-label={entry.label}
                        className={`${classes.dot} ${index === current ? classes.dotCurrent : ''}`}
                        onClick={() => setSlide(index)}
                    />
                ))}
                <Typography variant='caption' color='textSecondary' style={{ marginLeft: 12 }}>
                    {slides[current].label}
                </Typography>
            </div>
            </>}
        </div>
    );
}

export default MatchStreamPage;
